import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import { cn } from "@/core/utils/func/cn";
import type { ReportingGaugeDto } from "@/features/reporting/api/dto";
import { CRITICAL_GAUGE_NAMES } from "@/features/reporting/api/dto";

const GAUGE_HELP_KEYS: Record<string, TFunctionType> = {
  flashpay_wallet_stuck_awaiting_ledger:
    "dashboard.gauges.help.flashpay_wallet_stuck_awaiting_ledger",
  flashpay_ledger_balance_gap_minor:
    "dashboard.gauges.help.flashpay_ledger_balance_gap_minor",
  flashpay_kyc_pending_review: "dashboard.gauges.help.flashpay_kyc_pending_review",
  flashpay_merchant_active_without_settlement_wallet:
    "dashboard.gauges.help.flashpay_merchant_active_without_settlement_wallet",
  flashpay_fee_subscriptions_past_due:
    "dashboard.gauges.help.flashpay_fee_subscriptions_past_due",
};

type GaugeTone = "success" | "warning" | "destructive";

const gaugeTone = (name: string, value: number): GaugeTone => {
  if (name === "flashpay_ledger_balance_gap_minor") {
    return value === 0 ? "success" : "destructive";
  }
  if (name === "flashpay_wallet_stuck_awaiting_ledger") {
    return value > 0 ? "destructive" : "success";
  }
  return value > 0 ? "warning" : "success";
};

const TONE_CLASS: Record<GaugeTone, string> = {
  success: "border-success/40 bg-success-muted text-success",
  warning: "border-warning/40 bg-warning-muted text-warning",
  destructive: "border-destructive/40 bg-destructive/10 text-destructive",
};

const formatGaugeValue = (name: string, value: number): string => {
  if (name.endsWith("_minor")) return formatMinorAmount(value);
  return new Intl.NumberFormat().format(value);
};

/**
 * Bandeau des jauges critiques §7.5 — tokens success / warning / destructive.
 */
export function ReportingGaugesBanner({
  gauges,
  compact = false,
  names = CRITICAL_GAUGE_NAMES,
  className,
}: {
  gauges: ReportingGaugeDto[];
  compact?: boolean;
  names?: readonly string[];
  className?: string;
}) {
  const { t } = useTypedTranslation();
  const display = names
    .map((name) => gauges.find((gauge) => gauge.name === name))
    .filter((gauge): gauge is ReportingGaugeDto => Boolean(gauge));

  if (!display.length) {
    return compact ? null : (
      <p className="text-sm text-muted-foreground">{t("dashboard.gauges.empty")}</p>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-3",
        compact ? "grid-cols-1 sm:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-3",
        className
      )}
    >
      {display.map((gauge) => {
        const tone = gaugeTone(gauge.name, gauge.value);
        const Icon = tone === "success" ? CheckCircle2 : AlertTriangle;
        const helpKey = GAUGE_HELP_KEYS[gauge.name];
        return (
          <div
            key={gauge.name}
            className={cn(
              "flex gap-3 rounded-xl border p-3.5 shadow-sm",
              TONE_CLASS[tone]
            )}
          >
            <Icon className="mt-0.5 size-4 shrink-0" />
            <div className="flex min-w-0 flex-col gap-1">
              <NoTranslate as="span" className="text-sm font-semibold">
                {gauge.name}
              </NoTranslate>
              <NoTranslate as="span" className="text-lg font-medium">
                {formatGaugeValue(gauge.name, gauge.value)}
              </NoTranslate>
              <span className="text-xs text-foreground/80">
                {helpKey ? t(helpKey) : gauge.help ?? t("dashboard.gauges.alert")}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
